import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, Edit2, Trash2, X, ShieldCheck, Building2, UserRound, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminApi } from './useAdminApi';
import { useAuth } from '../../context/AuthContext';

// ── Role helpers ──────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Team Admin',
  user: 'User',
};

const ROLE_STYLES = {
  super_admin: 'bg-orange-primary/10 text-orange-primary border border-orange-primary/20',
  admin: 'bg-blue-50 text-blue-600 border border-blue-200',
  user: 'bg-charcoal/5 text-charcoal/50 border border-charcoal/10',
};

const ORG_STYLE = 'bg-charcoal/5 text-charcoal/60 border border-charcoal/10';

const UsersTab = () => {
  const { adminApi } = useAdminApi();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Add user modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Edit user modal
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: '', organisation: '', role: '' });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await adminApi('get', '/admin/users/details');
      setUsers(res.data);
    } finally {
      setLoading(false);
    }
  };

  // ── Filtered + sorted ─────────────────────────────────────────────────────
  const filtered = users.filter(u =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.organisation?.toLowerCase().includes(search.toLowerCase())
  );

  const admins = filtered.filter(u => u.role === 'super_admin' || u.role === 'admin');
  const regularUsers = filtered.filter(u => u.role === 'user' || !u.role);

  // ── Add user ──────────────────────────────────────────────────────────────
  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await adminApi('post', '/admin/users', { email: addEmail.trim() });
      toast.success(`${addEmail} added`);
      setAddEmail('');
      setShowAddModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit user ─────────────────────────────────────────────────────────────
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      display_name: u.display_name || u.name || '',
      organisation: u.organisation || 'Remote Conversations',
      role: u.role || 'user',
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await adminApi('put', `/admin/users/${editUser.id}`, {
        display_name: editForm.display_name || null,
        organisation: editForm.organisation,
        role: editForm.role,
      });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete user ───────────────────────────────────────────────────────────
  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await adminApi('delete', `/admin/users/${u.id}`);
      toast.success(`${u.email} deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const isSelf = (u) => u.email === currentUser?.email;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h2 className="font-serif text-xl text-charcoal">Users</h2>
          <p className="text-charcoal/50 text-sm mt-1">
            {users.length} total · {admins.length} admin{admins.length !== 1 ? 's' : ''} · {users.filter(u => u.role === 'user' || !u.role).length} regular
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              type="text"
              placeholder="Search users…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-lg border border-charcoal/10 bg-white/50 focus:outline-none text-sm"
              data-testid="users-search"
            />
          </div>
          <button
            onClick={() => { setAddEmail(''); setShowAddModal(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm"
            style={{ background: '#ff780a' }}
            data-testid="add-user-btn"
          >
            <Plus size={15} /> Add User
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {/* ── Admins section ── */}
          {admins.length > 0 && (
            <Section title="Admins" count={admins.length} icon={ShieldCheck} accent>
              {admins.map(u => (
                <UserRow key={u.id} u={u} isSelf={isSelf(u)}
                  onEdit={() => openEdit(u)} onDelete={() => handleDelete(u)} />
              ))}
            </Section>
          )}

          {/* ── Users section ── */}
          <Section title="Users" count={regularUsers.length} icon={UserRound}>
            {regularUsers.length === 0 ? (
              <div className="text-center py-8 text-charcoal/40 text-sm">No users found</div>
            ) : regularUsers.map(u => (
              <UserRow key={u.id} u={u} isSelf={isSelf(u)}
                onEdit={() => openEdit(u)} onDelete={() => handleDelete(u)} />
            ))}
          </Section>
        </>
      )}

      {/* ── Add User Modal ── */}
      <AnimatePresence>
        {showAddModal && (
          <Modal onClose={() => setShowAddModal(false)} title="Add User">
            <form onSubmit={handleAddUser}>
              <p className="text-charcoal/50 text-sm mb-5">
                Enter the user's email. They'll be added as a Remote Conversations user and can sign in via Magic Link.
              </p>
              <div className="mb-6">
                <label className="block text-sm text-charcoal/60 mb-2">Email address</label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={e => setAddEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="user@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                  data-testid="add-user-email-input"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="flex-1 py-3 rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal/5">Cancel</button>
                <button type="submit" disabled={addLoading}
                  className="flex-1 py-3 rounded-full text-white disabled:opacity-60"
                  style={{ background: '#ff780a' }} data-testid="add-user-submit">
                  {addLoading ? 'Adding…' : 'Add User'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* ── Edit User Modal ── */}
      <AnimatePresence>
        {editUser && (
          <Modal onClose={() => setEditUser(null)} title="Edit User">
            <form onSubmit={handleEditSave}>
              {/* Email — read-only */}
              <div className="mb-4">
                <label className="block text-sm text-charcoal/60 mb-2">Email</label>
                <div className="w-full px-4 py-3 rounded-xl border border-charcoal/5 bg-charcoal/3 text-charcoal/60 text-sm select-all">
                  {editUser.email}
                </div>
              </div>

              {/* Display name */}
              <div className="mb-4">
                <label className="block text-sm text-charcoal/60 mb-2">Display Name</label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={e => setEditForm(p => ({ ...p, display_name: e.target.value }))}
                  placeholder="Optional"
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                  data-testid="edit-display-name"
                />
              </div>

              {/* Organisation */}
              <div className="mb-4">
                <label className="block text-sm text-charcoal/60 mb-2">Organisation</label>
                <input
                  type="text"
                  value={editForm.organisation}
                  onChange={e => setEditForm(p => ({ ...p, organisation: e.target.value }))}
                  placeholder="Remote Conversations"
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
                  data-testid="edit-organisation"
                />
              </div>

              {/* Role */}
              <div className="mb-6">
                <label className="block text-sm text-charcoal/60 mb-2">Role</label>
                <div className="relative">
                  <select
                    value={editForm.role}
                    onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                    disabled={isSelf(editUser)}
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50 appearance-none disabled:opacity-50"
                    data-testid="edit-role-select"
                  >
                    <option value="user">User</option>
                    <option value="admin">Team Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal/40 pointer-events-none" />
                </div>
                {isSelf(editUser) && (
                  <p className="text-xs text-charcoal/40 mt-1">You cannot change your own role.</p>
                )}
                {editForm.role === 'super_admin' && !isSelf(editUser) && (
                  <p className="text-xs text-orange-primary/70 mt-1">Super Admins have full access to the admin panel.</p>
                )}
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setEditUser(null)}
                  className="flex-1 py-3 rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal/5">Cancel</button>
                <button type="submit" disabled={editLoading || isSelf(editUser) && editForm.role !== editUser.role}
                  className="flex-1 py-3 rounded-full text-white disabled:opacity-60"
                  style={{ background: '#ff780a' }} data-testid="edit-user-save">
                  {editLoading ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const Section = ({ title, count, icon: Icon, accent, children }) => (
  <div className={`mb-8 rounded-2xl overflow-hidden border ${accent ? 'border-orange-primary/15' : 'border-charcoal/8'}`}>
    <div className={`px-5 py-3 flex items-center gap-2 ${accent ? 'bg-orange-primary/5' : 'bg-charcoal/3'}`}>
      <Icon size={15} className={accent ? 'text-orange-primary' : 'text-charcoal/40'} />
      <span className={`text-sm font-medium ${accent ? 'text-orange-primary' : 'text-charcoal/60'}`}>{title}</span>
      <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${accent ? 'bg-orange-primary/10 text-orange-primary' : 'bg-charcoal/8 text-charcoal/50'}`}>{count}</span>
    </div>
    <div className="divide-y divide-charcoal/5">{children}</div>
  </div>
);

const UserRow = ({ u, isSelf, onEdit, onDelete }) => {
  const lastActive = u.last_active
    ? new Date(u.last_active).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
    : 'Never';

  return (
    <div
      data-testid={`user-row-${u.id}`}
      className="px-5 py-4 flex items-center gap-4 bg-white/40 hover:bg-white/60 transition-colors"
    >
      {/* Avatar placeholder */}
      <div className="w-8 h-8 rounded-full bg-charcoal/8 flex items-center justify-center flex-shrink-0 text-charcoal/40 text-sm font-medium uppercase">
        {u.email?.[0] || '?'}
      </div>

      {/* Identity */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-charcoal text-sm truncate">{u.email}</span>
          {isSelf && <span className="text-xs text-charcoal/40">(you)</span>}
          {!u.has_signed_in && <span className="text-xs text-charcoal/30 italic">not signed in</span>}
        </div>
        {(u.name || u.display_name) && (
          <div className="text-xs text-charcoal/50 mt-0.5">{u.display_name || u.name}</div>
        )}
      </div>

      {/* Org badge */}
      <span className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${ORG_STYLE}`}>
        <Building2 size={11} className="flex-shrink-0" />
        {u.organisation || 'Remote Conversations'}
      </span>

      {/* Role badge */}
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${ROLE_STYLES[u.role] || ROLE_STYLES.user}`}
        data-testid={`role-badge-${u.id}`}>
        {ROLE_LABELS[u.role] || 'User'}
      </span>

      {/* Stats */}
      <div className="hidden lg:flex items-center gap-4 text-xs text-charcoal/40 flex-shrink-0">
        <span>{u.total_points} pts</span>
        <span>{lastActive}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-2 rounded-lg hover:bg-charcoal/5 transition-colors"
          title="Edit user"
          data-testid={`edit-user-${u.id}`}
        >
          <Edit2 size={15} className="text-charcoal/50" />
        </button>
        {!isSelf && (
          <button
            onClick={onDelete}
            className="p-2 rounded-lg hover:bg-red-50 transition-colors"
            title="Delete user"
            data-testid={`delete-user-${u.id}`}
          >
            <Trash2 size={15} className="text-red-400" />
          </button>
        )}
      </div>
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    style={{ backgroundColor: 'rgba(44, 36, 23, 0.5)' }}
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
      className="w-full max-w-lg rounded-2xl shadow-2xl p-6"
      style={{ backgroundColor: '#FFF8F0' }}
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-xl text-charcoal">{title}</h3>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-charcoal/5">
          <X size={20} className="text-charcoal/60" />
        </button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);

export default UsersTab;
